import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LOGO_SHIELD } from "@/lib/api";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/admin/Dashboard";
import Calendar from "@/pages/admin/Calendar";
import Customers from "@/pages/admin/Customers";
import Jobs from "@/pages/admin/Jobs";
import Quotes from "@/pages/admin/Quotes";
import WeeklyBilling from "@/pages/admin/WeeklyBilling";
import InvoicesAwaitingPayment from "@/pages/admin/InvoicesAwaitingPayment";
import WaitingToInvoice from "@/pages/admin/WaitingToInvoice";
import PriceList from "@/pages/admin/PriceList";
import Materials from "@/pages/admin/Materials";
import CalendarSettings from "@/pages/admin/CalendarSettings";
import Employees from "@/pages/admin/Employees";
import Reports from "@/pages/admin/Reports";
import Cashflow from "@/pages/admin/Cashflow";
import Settings from "@/pages/admin/Settings";
import SettingsHub from "@/pages/admin/SettingsHub";
import XeroSettings from "@/pages/admin/XeroSettings";
import EmailTemplates from "@/pages/admin/EmailTemplates";
import JobConfirmations from "@/pages/admin/JobConfirmations";

import FinancialSettings from "@/pages/admin/FinancialSettings";
import Branding from "@/pages/admin/Branding";
import Integrations from "@/pages/admin/Integrations";
import JobDetail from "@/pages/JobDetail";
import MyJobs from "@/pages/tech/MyJobs";
import SignDocket from "@/pages/SignDocket";
import Onboard from "@/pages/Onboard";
import ResetPassword from "@/pages/ResetPassword";
import ContractorPortal from "@/pages/ContractorPortal";
import ContractorJob from "@/pages/ContractorJob";
import TimesheetsRoute from "@/pages/TimesheetsRoute";
import LeaveRoute from "@/pages/LeaveRoute";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-navy">
      <img src={LOGO_SHIELD} alt="loading" className="w-20 h-20 animate-pulse" />
    </div>
  );
}

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "contractor") return <Navigate to="/onboarding" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/my-jobs" replace />;
  return children;
}

function ContractorRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "contractor") return <Navigate to="/" replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  const dest = user.role === "admin" ? "/dashboard" : user.role === "contractor" ? "/onboarding" : "/my-jobs";
  return <Navigate to={dest} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/sign/:token" element={<SignDocket />} />
            <Route path="/onboard/:token" element={<Onboard />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/onboarding" element={<ContractorRoute><ContractorPortal /></ContractorRoute>} />
            <Route path="/field/job/:id" element={<ContractorRoute><ContractorJob /></ContractorRoute>} />
            <Route path="/" element={<RootRedirect />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/dashboard" element={<Protected adminOnly><Dashboard /></Protected>} />
              <Route path="/calendar" element={<Protected adminOnly><Calendar /></Protected>} />
              <Route path="/customers" element={<Protected adminOnly><Customers /></Protected>} />
              <Route path="/jobs" element={<Protected adminOnly><Jobs /></Protected>} />
              <Route path="/quotes" element={<Protected adminOnly><Quotes /></Protected>} />
              <Route path="/weekly-billing" element={<Protected adminOnly><WeeklyBilling /></Protected>} />
              <Route path="/invoices-awaiting-payment" element={<Protected adminOnly><InvoicesAwaitingPayment /></Protected>} />
              <Route path="/waiting-to-invoice" element={<Protected adminOnly><WaitingToInvoice /></Protected>} />
              <Route path="/price-list" element={<Protected adminOnly><PriceList /></Protected>} />
              <Route path="/holidays-rdo" element={<Protected adminOnly><CalendarSettings /></Protected>} />
              <Route path="/materials" element={<Protected adminOnly><Materials /></Protected>} />
              <Route path="/employees" element={<Protected adminOnly><Employees /></Protected>} />
              <Route path="/reports" element={<Protected adminOnly><Reports /></Protected>} />
              <Route path="/reports/cashflow" element={<Protected adminOnly><Cashflow /></Protected>} />
              <Route path="/settings" element={<Protected adminOnly><SettingsHub /></Protected>} />
              <Route path="/settings/company" element={<Protected adminOnly><Settings /></Protected>} />
              <Route path="/settings/xero" element={<Protected adminOnly><XeroSettings /></Protected>} />
              <Route path="/settings/reminder-template" element={<Protected adminOnly><EmailTemplates /></Protected>} />
              <Route path="/settings/email" element={<Protected adminOnly><EmailTemplates /></Protected>} />
              <Route path="/job-confirmations" element={<Protected adminOnly><JobConfirmations /></Protected>} />
              <Route path="/settings/financial" element={<Protected adminOnly><FinancialSettings /></Protected>} />
              <Route path="/settings/branding" element={<Protected adminOnly><Branding /></Protected>} />
              <Route path="/settings/integrations" element={<Protected adminOnly><Integrations /></Protected>} />
              <Route path="/my-jobs" element={<MyJobs />} />
              <Route path="/timesheets" element={<TimesheetsRoute />} />
              <Route path="/leave" element={<LeaveRoute />} />
              <Route path="/jobs/:id" element={<JobDetail />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors duration={2500} closeButton />
      </AuthProvider>
    </div>
  );
}

export default App;
